import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import { api } from '../lib/api.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import {
  coverageTwoWays, daysWord, WINDOW_LABEL, SIZE_LABEL, sizeLabel, filterOffers, sortOffers,
  SIZE_CHANGED_HEADING, SIZE_CHANGED_FALLBACK, SHORTFALL_HEADING, SHORTFALL_FALLBACK,
} from '../lib/tradeDesk.js'
import DateFilter from '../components/DateFilter.jsx'
import { useDateFilter } from '../lib/useDateFilter.js'
import styles from './TradeDesk.module.css'

/**
 * THE TRADE DESK.
 *
 * Brian, 2026-10-16, defining it himself: *"What is a trade desk. It's a place
 * where our team sees the championship opportunities their coworkers are asking
 * to trade. It must be done prior to championship happening. Or there needs to
 * be an executive override. We need to have KPI's here. we need to know we've
 * got full real coverage with a plan."*
 *
 * ⭐ THE OFFER BOARD IS THE PAGE, AND THAT ORDERING IS THE DESIGN DECISION.
 * The thing a rep opens this for is to see what their COWORKERS are asking to
 * trade, so that is the first thing on the screen and it is not filtered by
 * viewer — seeing somebody else's offers IS the feature. The contested-crown
 * queue and the escalations are a second lane further down. Built the other way
 * round this would have been a manager's conflict queue with an offer list
 * bolted on, which is a different product.
 *
 * ⭐ THE SCOPE IS THE UNIT, NOT THE RECORD. Brian: *"a rep should be able to
 * trade from entire governing bodies down to a single championship."* So a card
 * reads "TAPPS — all 118 records" or "UIL Texas 5A football — 1 record", and
 * the count is RE-RESOLVED on every read: if a body-level offer has changed
 * size since it was posted, the card says so above the claim button rather than
 * after somebody has accepted it.
 *
 * ⭐ THE WINDOW IS A STATE, NOT A WARNING. *"It must be done prior to
 * championship happening. Or there needs to be an executive override."* So an
 * offer whose earliest championship has passed does not show a claim button at
 * all — it shows the executive move, and for a rep it shows the route to ask
 * for one. The state is computed from the record's own event date on every
 * read, so an offer cannot be left open past its event by a sweep that failed
 * to run.
 *
 * ⭐ THE TWO COVERAGE NUMBERS, AND THE GAP IS THE HEADLINE.
 * Brian: *"we need to know we've got full real coverage with a plan."* Assigned
 * is not covered. Counted as "has an owner" the number flatters; counted as
 * "owner plus evidence of work" it is true and much lower. Both are shown, and
 * the GAP carries the accent because the gap is the work.
 *
 * ⚠️ AND THE PLAN DEFINITION COMES FROM `lib/coverage.js`, NOT FROM HERE.
 * That module already defines the funnel and already measured it live over
 * 5,000 records; Brian asked for the same signals he asked for on bowl
 * eligibility. Declaring a second definition of "covered" on this page is the
 * defect, not the fix. It is also why the signal list shows `measurable` next
 * to each count: most of the lifecycle has NO FIELD, so those zeros are a
 * schema gap and not a performance one, and a KPI that cannot tell those apart
 * is the flattering kind Brian said is worse than none.
 *
 * ⭐ NO DEAD ENDS. Every open card carries its controls, and a control that
 * cannot be used prints the reason underneath itself instead of greying out.
 *
 * ⭐ AND IT IS A READ PLUS FOUR NAMED WRITES. The board computes nothing it
 * stores. The writes — offer, claim, withdraw, escalate, executive move — all
 * re-decide authority server-side; this page's job is to collect the mandatory
 * reason before sending, not to decide who may.
 */

/* ⚠️ HOISTED, for the reason BowlEligibility.jsx gives in its own header:
 * check-clickable.mjs reads the word "card" in a className inside a map and
 * reports the SECTION as an inert record row. One constant removes the misfire
 * without weakening the gate. */
const PANEL = `card ${styles.panel}`

const errOf = (e, fallback) =>
  e?.body?.reason || e?.body?.detail || e?.body?.error || e?.message || fallback

/**
 * ⚠️ "NOT DEPLOYED" IS A THIRD ANSWER, DISTINCT FROM A FAILED READ AND FROM AN
 * EMPTY BOARD — and getting that wrong is the whole failure this app keeps
 * finding. The frontend and the backend ship separately here, so a tab can be
 * live against a backend that has not yet taken the routes it calls. That
 * answers 404/403-unmapped, which rendered as a generic failure would read to a
 * rep as "the desk is broken" and to whoever is debugging it as a bug in the
 * board rather than a deployment that has not happened.
 *
 * So it is named, with the remedy, and it is NOT an error the reader is asked
 * to retry forever.
 */
const NOT_DEPLOYED =
  'The trade desk service is not on this backend yet. The screen is live and correct; the routes '
  + 'it reads (/api/trade-desk) ship with the sr-portal-backend lane/trade-desk branch, which has '
  + 'to be merged and deployed by somebody who can push. This is NOT an empty board and NOT a '
  + 'fault in this page — the coverage figures above are read from a different route and are live.'

const looksUndeployed = (e) => {
  const s = Number(e?.status || e?.body?.status || 0)
  const code = String(e?.body?.error || '')
  return s === 404 || /not_found|unknown_route|no_rule|unmapped/i.test(code)
}

/** A number with its denominator. Never a bare count. */
function Figure({ n, of, suffix = '' }) {
  return (
    <>
      <span className={styles.kpiNumber}>{n == null ? '—' : n.toLocaleString()}{suffix}</span>
      <span className={styles.kpiOf}>
        {of == null ? 'no denominator available' : `of ${of.toLocaleString()}`}
      </span>
    </>
  )
}

export default function TradeDesk() {
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)

  const [desk, setDesk] = useState({ status: 'loading', data: null, error: '' })
  /* A SEPARATE read, and its failure is reported separately: the board and the
   * coverage strip answer different questions, and one being unavailable must
   * not make the other look empty. */
  const [feed, setFeed] = useState({ status: 'loading', data: null, error: '' })
  const [filters, setFilters] = useState({ body: '', state: '', sport: '', size: '', offeredBy: '' })
  const [busy, setBusy] = useState('')
  const [notice, setNotice] = useState(null)
  const [showRules, setShowRules] = useState(false)

  const load = useCallback(() => {
    setDesk((s) => ({ ...s, status: s.data ? 'refreshing' : 'loading' }))
    api.tradeDesk()
      .then((d) => setDesk({ status: 'ready', data: d, error: '' }))
      .catch((e) => setDesk({
        status: looksUndeployed(e) ? 'undeployed' : 'error',
        data: null,
        error: looksUndeployed(e)
          ? NOT_DEPLOYED
          : errOf(e, 'The trade desk could not be read. This is a refusal, not an empty board.'),
      }))
    /* 🔴 WAS `api.championshipsFeed()`, WHICH IS THE INGEST REPORT AND NOT A
     * RECORD LIST. It answers 200 with { ranAt, created, corrected, flagged,
     * champions, refused } — no `records`, `results` or `rows` — so `records`
     * below resolved to [] and all three tiles rendered "0 of 0" as a
     * successful measurement of an empty universe (measured 2026-10-16 against
     * backend 4135649; the object held 15,865 records the same minute). See the
     * note on
     * `tradeDeskCoverageFeed` in src/lib/api.js. */
    api.tradeDeskCoverageFeed()
      .then((d) => setFeed({ status: 'ready', data: d, error: '' }))
      .catch((e) => setFeed({ status: 'error', data: null,
        error: errOf(e, 'The championship feed could not be read, so coverage is unavailable.') }))
  }, [])

  useEffect(() => { if (allowed) load() }, [allowed, load])

  /* ⭐ `/trade-desk?offer=<id>` — THE DOOR THE LINKS USE (2026-10-19). A flagged
   * trade on the worklist, a Command Center row or a governing-body card sends
   * the reader here to Withdraw / Counter / Send to Brian, and arriving at the
   * top of a long board with no idea which card was meant would be a door into
   * a hallway. So the card is scrolled to and marked. */
  const [params] = useSearchParams()
  const focusOffer = params.get('offer') || ''
  useEffect(() => {
    if (!focusOffer || desk.status !== 'ready') return
    const el = document.getElementById(`offer-${focusOffer}`)
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [focusOffer, desk.status])

  const d = desk.data
  /* The shared date search on the championship date each offer closes on
   * (2026-10-26, [Jarvis · Claude]). An offer with no readable date stays. */
  const df = useDateFilter('trades')
  const datedOffers = useMemo(() => df.apply(sortOffers(filterOffers(d?.offers || [], filters))),
    [d, filters, df])
  const offers = datedOffers.rows

  const records = useMemo(() => {
    const f = feed.data
    return f?.records || f?.results || f?.rows || (Array.isArray(f) ? f : [])
  }, [feed.data])

  const signalsNeeded = d?.rules?.kpis?.planSignalsRequired ?? 1
  const cov = useMemo(() => coverageTwoWays(records, { signalsNeeded }),
    [records, signalsNeeded])

  /* ---- THE WRITES. Each collects the mandatory reason FIRST, because the
   * server answers 400 reason_required without it and discovering that as an
   * error is the shape that 400'd 9,774 assignments on 2026-09-30. ------- */
  const act = useCallback(async (label, fn) => {
    setBusy(label)
    setNotice(null)
    try {
      const r = await fn()
      setNotice({ tone: 'ok', text: r?.note || r?.reason || 'Done.' })
      load()
    } catch (e) {
      setNotice({ tone: 'bad', text: errOf(e, 'That was refused and nothing changed.') })
    } finally {
      setBusy('')
    }
  }, [load])

  const askReason = (prompt) => {
    /* ⚠️ The `// eslint-disable-next-line no-alert` that was here is removed,
     * not moved: `no-alert` is not enabled in this repo's eslint config, so the
     * directive was itself an ERROR ("unused eslint-disable directive") and it
     * failed `npm run build` at the lint gate on 2026-10-16 — which means
     * 2.119.0 cannot have been published through this gate as it stands. Left
     * as a note because a future lane adding `no-alert` will need the
     * directive back, and deleting a comment with no explanation is how that
     * gets rediscovered the hard way. */
    const r = window.prompt(prompt)
    return r == null ? null : String(r).trim()
  }

  const onClaim = (o) => {
    const reason = askReason(
      `Claim ${o.scopeLabel}.\n\nWhy are you taking this on? (required — it goes on the permanent `
      + 'trade ledger)')
    if (!reason) return
    act(`claim:${o.id}`, () => api.tradeDeskClaim({
      offerId: o.id, reason, acceptSizeChange: o.sizeChanged === true,
    }))
  }
  const onCounter = (o) => {
    const sport = askReason(
      `Counter ${o.scopeLabel} at a narrower scope.\n\nWhich SPORT do you want? (a counter may `
      + 'only narrow the offer; the rest of it stays open)')
    if (!sport) return
    const reason = askReason('Why this part only? (required)')
    if (!reason) return
    act(`counter:${o.id}`, () => api.tradeDeskOffer({
      body: o.scope?.body, state: o.scope?.state || o.state, sport, reason, counterOf: o.id,
    }))
  }
  const onWithdraw = (o) => {
    const reason = askReason(`Withdraw ${o.scopeLabel}.\n\nWhy? (required)`)
    if (!reason) return
    act(`withdraw:${o.id}`, () => api.tradeDeskWithdraw({ offerId: o.id, reason }))
  }
  const onEscalate = (o) => {
    const reason = askReason(
      `Send ${o.scopeLabel} to ${d?.rules?.executiveOverride?.executive || 'the executive'}.`
      + '\n\nWhat do you need decided? (required)')
    if (!reason) return
    act(`escalate:${o.id}`, () => api.tradeDeskEscalate({ offerId: o.id, reason }))
  }
  const onExecutiveMove = (o) => {
    const toRep = askReason(
      `EXECUTIVE MOVE on ${o.scopeLabel}.\n\nWhich rep does it go to? (exact roster name)\n\n`
      + (d?.roster || []).join('\n'))
    if (!toRep) return
    const reason = askReason(
      'Why? (required — this is recorded as an executive move with your name on it, permanently, '
      + 'and after the championship it affects credit on work already won)')
    if (!reason) return
    act(`exec:${o.id}`, () => api.tradeDeskExecutiveMove({ offerId: o.id, toRep, reason }))
  }

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="Trading" title="Trade Desk" />
        <div className={PANEL}>
          <p>{why?.(ACTIONS.CC_VIEW) || 'You do not have permission to view this.'}</p>
        </div>
      </>
    )
  }

  const control = (o, c) => {
    const key = `${c.kind}:${o.id}`
    const onClick = c.kind === 'claim' ? () => onClaim(o)
      : c.kind === 'counter' ? () => onCounter(o)
        : c.kind === 'withdraw' ? () => onWithdraw(o)
          : c.kind === 'escalate' ? () => onEscalate(o)
            : c.kind === 'executive-move' ? () => onExecutiveMove(o)
              : null
    const isBusy = busy === key || busy === `exec:${o.id}`
    return (
      <div key={c.kind}>
        <button
          type="button"
          className={c.kind === 'claim' || c.kind === 'executive-move' ? 'btn btn-primary' : 'btn'}
          onClick={onClick}
          disabled={!c.available || isBusy || !onClick}
          aria-describedby={c.why ? `${o.id}-${c.kind}-why` : undefined}
        >
          {isBusy ? 'Working…' : c.label}
        </button>
        {/* ⚠️ THE REASON IS RENDERED, ALWAYS. A greyed button with no sentence
            under it is the dead end this desk is named after. */}
        {c.why ? (
          <p className={styles.blocked} id={`${o.id}-${c.kind}-why`}>{c.why}</p>
        ) : null}
      </div>
    )
  }

  const OfferCard = (o) => (
    <article className={styles.offer} key={o.id} id={`offer-${o.id}`}
      data-focus={focusOffer === o.id ? '1' : undefined}
      style={focusOffer === o.id ? { outline: '3px solid var(--brass)', outlineOffset: 2 } : undefined}>
      <div className={styles.offerHead}>
        <h3 className={styles.scopeLabel}>{o.scopeLabel}</h3>
        <span className="chip">{WINDOW_LABEL[o.window?.state] || o.window?.state}</span>
      </div>

      <div className={styles.chips}>
        <span className="chip">{sizeLabel(o.scopeLevel, o.scopeLevelLabel)}</span>
        {o.body ? <span className="chip">{o.body}</span> : null}
        {o.state ? <span className="chip">{o.state}</span> : null}
        {o.sport ? <span className="chip">{o.sport}</span> : null}
      </div>

      <p className={styles.meta}>
        <strong>{o.offeredBy}</strong> is offering this
        {o.currentOwner && o.currentOwner !== o.offeredBy
          ? <> — currently held by <strong>{o.currentOwner}</strong></> : null}
        .{' '}
        <span className={styles.metaMuted}>
          {o.window?.governedBy
            ? `Earliest championship ${daysWord(o.window.daysToEvent)}.`
            : 'No readable event date on this offer.'}
        </span>
      </p>

      {/* The event date, its basis, and whether it is projected — stated on the
          card BEFORE the rep acts, never discovered in a refusal. */}
      {o.window?.governedByUnsourced ? (
        <p className={styles.warn}>
          The date governing this window is PROJECTED, not sourced. It does not block the trade and
          it does not authorise it — the trade is allowed and will be recorded as resting on a
          projected date.
        </p>
      ) : null}
      {o.window?.undatedNote ? <p className={styles.warn}>{o.window.undatedNote}</p> : null}

      {o.whyOffered ? <p className={styles.why}>“{o.whyOffered}”</p> : null}

      {/* WHAT IT COVERS, AND WHAT IT CANNOT REACH. */}
      <p className={styles.meta}>
        Covers <strong>{o.size?.heldNow ?? '—'}</strong>
        {o.size?.coveredNow != null && o.size.coveredNow !== o.size.heldNow
          ? <> of {o.size.coveredNow} in scope</> : null}
        .{' '}
        <span className={styles.metaMuted}>
          Work recorded on {o.workDone?.readyForArtwork ?? 0} of {o.workDone?.of ?? 0}
          {' '}(artwork — the one plan signal with a field today).
        </span>
      </p>
      {/* ⚠️ HEADING OURS, DETAIL THE SERVER'S. Written as a bare
          `{o.size.shortfall}` the whole warning vanished the moment the field
          was absent — and the absence would have read as "covers everything". */}
      {(o.size?.shortfall || (o.size?.coveredNow != null && o.size.coveredNow !== o.size.heldNow)) ? (
        <p className={styles.warn}>
          <strong>{SHORTFALL_HEADING}.</strong>{' '}
          {o.size?.shortfall || SHORTFALL_FALLBACK}
        </p>
      ) : null}

      {/* ⚠️ THE SIZE CHANGE, ABOVE THE BUTTONS, NEVER BELOW THEM. Brian: say
          so on the card before anyone accepts. */}
      {o.sizeChanged ? (
        <p className={`${styles.warn} ${styles.stop}`}>
          <strong>{SIZE_CHANGED_HEADING}.</strong>{' '}
          {o.sizeChangeNote || SIZE_CHANGED_FALLBACK}
        </p>
      ) : null}

      {o.settled ? (
        <p className={styles.warn}>
          Settled by {o.settled.kind} — <strong>{o.settled.by}</strong>, {o.settled.at}.
          {' '}“{o.settled.reason}”
          {o.settled.postEvent ? ' Made after the championship, so it affects credit.' : null}
        </p>
      ) : null}
      {o.escalated ? (
        <p className={styles.warn}>
          Sent to the executive by <strong>{o.escalated.by}</strong>. “{o.escalated.reason}”
        </p>
      ) : null}

      {o.unreadable ? <p className={`${styles.warn} ${styles.stop}`}>{o.unreadable}</p> : null}

      <div className={styles.controls}>
        {(o.controls || []).map((c) => control(o, c))}
      </div>
    </article>
  )

  return (
    <>
      <PageHeader
        eyebrow="Trading"
        title="Trade Desk"
        blurb="What your coworkers are asking to trade — a whole governing body down to one championship. A trade completes before the championship happens, or it takes an executive."
      />

      {notice ? (
        <div className={`${PANEL} ${notice.tone === 'bad' ? styles.stateErr : ''}`} role="status">
          <p className={styles.stateBody}>{notice.text}</p>
        </div>
      ) : null}

      {/* ================= COVERAGE — THE GAP IS THE HEADLINE ============ */}
      <section className={PANEL}>
        <p className="eyebrow">Real coverage</p>
        <hr className="rule" />
        {feed.status === 'loading' ? (
          <div className={styles.state}>
            <span className="spinner" aria-hidden="true" />
            <p className={styles.stateBody}>Reading the championship feed to work out coverage…</p>
          </div>
        ) : feed.status === 'error' ? (
          <div className={`${styles.state} ${styles.stateErr}`}>
            <p className={styles.stateTitle}>Coverage is unavailable</p>
            <p className={styles.stateBody}>
              {feed.error} This is a refusal, not a coverage of zero — do not read it as nothing
              being covered.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.kpis}>
              <div className={styles.kpi}>
                <p className="eyebrow">{cov.owned.label}</p>
                <Figure n={cov.owned.n} of={cov.owned.of} />
                <p className={styles.kpiReading}>{cov.owned.reading}</p>
              </div>
              <div className={styles.kpi}>
                <p className="eyebrow">{cov.ownedWithPlan.label}</p>
                <Figure n={cov.ownedWithPlan.n} of={cov.ownedWithPlan.of} />
                <p className={styles.kpiReading}>{cov.ownedWithPlan.reading}</p>
              </div>
              <div className={`${styles.kpi} ${styles.kpiGap}`}>
                <p className="eyebrow">{cov.gap.label}</p>
                <Figure n={cov.gap.n} of={cov.gap.of} />
                <p className={styles.kpiReading}>{cov.gap.reading}</p>
              </div>
            </div>

            {/* ⚠️ THE DENOMINATOR, AND WHETHER IT IS THE WHOLE OBJECT. Added
              * 2026-10-16. Every tile above reconciles against this number, so
              * it is printed rather than trusted — and if the feed walked only
              * part of the object, that is said HERE, next to the figures,
              * instead of being discoverable only in a network tab. A partial
              * read makes every number a floor twice over. */}
            {feed.data?.complete === false ? (
              <p className={`${styles.recon} ${styles.stateErr}`}>
                ⚠️ PARTIAL READ — these figures are computed over{' '}
                {Number(records.length).toLocaleString()} of{' '}
                {Number(feed.data?.universe ?? records.length).toLocaleString()} championships.{' '}
                {feed.data?.why || 'The feed did not finish walking the object.'} Treat every
                number above as a floor on a floor.
              </p>
            ) : (
              <p className={styles.recon}>
                Read over all {Number(records.length).toLocaleString()} championships on the
                object, walked to exhaustion — never the first page of n. Every figure above
                reconciles against that denominator.
              </p>
            )}

            {/* ⚠️ THE CEILING ON THE HONEST NUMBER, SAID OUT LOUD. */}
            <p className={styles.recon}>{cov.ceiling.why}</p>

            <ul className={styles.signals}>
              {cov.signals.map((s) => (
                <li key={s.id} className={`${styles.signal} ${s.measurable ? '' : styles.signalBlind}`}>
                  <span className={styles.signalN}>
                    {s.measurable ? `${s.n}/${s.of}` : 'n/a'}
                  </span>
                  <span>
                    <strong>{s.label}</strong> — {s.reading}
                  </span>
                </li>
              ))}
            </ul>
            <p className={styles.recon}>
              {cov.owned.counts}; {cov.ownedWithPlan.counts}. Read from the same championship feed
              the Command Center draws, and scored by the one definition in lib/coverage.js so the
              two surfaces cannot disagree.
            </p>
          </>
        )}
      </section>

      {/* ================= THE DESK'S OWN NUMBERS ======================= */}
      {d?.metrics ? (
        <section className={PANEL}>
          <p className="eyebrow">Desk</p>
          <hr className="rule" />
          <ul className={styles.signals}>
            {Object.entries(d.metrics)
              .filter(([, m]) => m && typeof m === 'object' && typeof m.n === 'number')
              .map(([k, m]) => (
                <li key={k} className={styles.signal}>
                  <span className={styles.signalN}>{m.n}/{m.of ?? '—'}</span>
                  <span><strong>{m.counts}</strong> <span className={styles.metaMuted}>— {m.source}</span></span>
                </li>
              ))}
          </ul>
          {d.read?.why ? <p className={styles.recon}>⚠️ {d.read.why}</p> : (
            <p className={styles.recon}>
              Reconciled: {d.read?.offersTotal} offers and {d.read?.championshipsIndexed}{' '}
              championships read in full over {d.read?.indexPages} pages, as of{' '}
              {d.read?.indexReadAt}. Nothing capped.
            </p>
          )}
        </section>
      ) : null}

      {/* ================= THE OFFER BOARD — THE PAGE =================== */}
      <section className={PANEL}>
        <p className="eyebrow">
          On the board <span className={styles.sectionCount}>
            {d ? `${offers.length} of ${d.offers?.length ?? 0}` : ''}
          </span>
        </p>
        <hr className="rule" />

        {d?.facets ? (
          <div className={styles.filters}>
            {[['body', 'Body'], ['state', 'State'], ['sport', 'Sport'],
              ['size', 'Size'], ['offeredBy', 'Offered by']].map(([key, label]) => (
              <label key={key}>
                <span className="field-label">{label}</span>
                <select
                  value={filters[key]}
                  onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                >
                  <option value="">All</option>
                  {(d.facets[key] || []).map((o) => (
                    <option key={String(o.value)} value={String(o.value)}>
                      {key === 'size' ? sizeLabel(o.value, o.label) : (o.label || o.value)} ({o.n})
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        ) : null}

        {desk.status === 'ready' ? (
          <DateFilter {...df.props} summary={datedOffers.summary} noun="offers" />
        ) : null}

        {desk.status === 'loading' ? (
          <div className={styles.state}>
            <span className="spinner" aria-hidden="true" />
            <p className={styles.stateTitle}>Reading the board</p>
            <p className={styles.stateBody}>
              Resolving every open offer against the live championship object, so a body-level
              offer shows what it covers right now rather than what it covered when it was posted.
            </p>
          </div>
        ) : desk.status === 'undeployed' ? (
          <div className={styles.state}>
            <p className={styles.stateTitle}>The board is waiting on its backend</p>
            <p className={styles.stateBody}>{desk.error}</p>
          </div>
        ) : desk.status === 'error' ? (
          <div className={`${styles.state} ${styles.stateErr}`}>
            <p className={styles.stateTitle}>The board could not be read</p>
            <p className={styles.stateBody}>{desk.error}</p>
            <p className={styles.stateBody}>
              <strong>This is a refusal, not an empty board.</strong> Do not record it as “no
              offers”.
            </p>
            <div className={styles.controls}>
              <button type="button" className="btn btn-primary" onClick={load}>Try again</button>
            </div>
          </div>
        ) : offers.length === 0 ? (
          <div className={styles.state}>
            <p className={styles.stateTitle}>
              {(d?.offers?.length ?? 0) > 0 ? 'Nothing matches those filters' : 'Nobody is offering anything'}
            </p>
            <p className={styles.stateBody}>
              {(d?.offers?.length ?? 0) > 0
                ? `There are ${d.offers.length} offers on the board; none match the filters above. Clear them to see the rest.`
                : 'The board was read successfully and no rep currently has an offer up. That is a '
                  + 'real zero, not a failed read — the read is reconciled above.'}
            </p>
            {(d?.offers?.length ?? 0) > 0 ? (
              <div className={styles.controls}>
                <button
                  type="button" className="btn"
                  onClick={() => setFilters({ body: '', state: '', sport: '', size: '', offeredBy: '' })}
                >Clear filters</button>
              </div>
            ) : null}
          </div>
        ) : (
          offers.map(OfferCard)
        )}
      </section>

      {/* ============ CLOSED BY THE EVENT — EXECUTIVE ONLY ============== */}
      {d?.closedAtEvent?.length ? (
        <section className={PANEL}>
          <p className="eyebrow">
            The championship has happened <span className={styles.sectionCount}>
              {d.closedAtEvent.length}
            </span>
          </p>
          <hr className="rule" />
          <p className={styles.stateBody}>
            The ordinary trade path is closed on these. Only an executive move changes ownership
            now, and it is recorded as an executive move because it affects credit on work already
            won.
          </p>
          {d.closedAtEvent.map(OfferCard)}
        </section>
      ) : null}

      {/* ================= ESCALATED TO THE EXECUTIVE =================== */}
      {d?.escalations?.length ? (
        <section className={PANEL}>
          <p className="eyebrow">
            Waiting on {d.rules?.executiveOverride?.executive || 'the executive'}
            <span className={styles.sectionCount}> {d.escalations.length}</span>
          </p>
          <hr className="rule" />
          <ul className={styles.ledger}>
            {d.escalations.map((a) => (
              <li key={a.targetId} className={styles.entry}>
                <div className={styles.entryHead}>
                  <span className={styles.entryMove}>{a.targetKind} {a.targetId}</span>
                  <span className={styles.entryWhen}>{a.escalatedAt}</span>
                </div>
                <p className={styles.ruleSays}>
                  Raised by {a.escalatedName || a.escalatedBy}. “{a.escalateReason}”
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ================= THE PERMANENT RECORD ========================= */}
      <section className={PANEL}>
        <p className="eyebrow">
          Trade ledger <span className={styles.sectionCount}>
            {d?.ledgerRead ? `${d.ledgerRead.returned} of ${d.ledgerRead.total}` : ''}
          </span>
        </p>
        <hr className="rule" />
        <p className={styles.stateBody}>
          Append-only. Every transition, with the scope and the record count as they read at the
          time — so “how did this end up assigned to Chris” is answerable from here months later
          rather than from memory.
        </p>
        {d?.ledgerRead?.why ? <p className={styles.recon}>⚠️ {d.ledgerRead.why}</p> : null}
        {/* 🔴 FIXED 2026-10-19. With the board read FAILED (504 on Brian's
            browser that day), `d` was null and this panel still rendered "no
            durable store" AND "the ledger was read and is empty" — two
            confident facts about a read that never happened, under a refusal
            two sections up. An unread ledger now says it is unread. */}
        {!d ? (
          <div className={styles.state}>
            <p className={styles.stateTitle}>
              {desk.status === 'loading' ? 'Reading the ledger…' : 'The ledger was not read'}
            </p>
            <p className={styles.stateBody}>
              {desk.status === 'loading'
                ? 'It arrives with the board.'
                : 'It is served with the board, and the board read was refused — so nothing here '
                  + 'is a statement about the ledger. Use “Try again” above.'}
            </p>
          </div>
        ) : !d?.ledgerRead?.durable ? (
          <p className={styles.recon}>
            ⚠️ The ledger has no durable store behind it right now, so what is shown is an
            in-memory mirror and is NOT a permanent record. Do not treat it as the history.
          </p>
        ) : null}
        {!d ? null : (d?.ledger?.length ?? 0) === 0 ? (
          <div className={styles.state}>
            <p className={styles.stateTitle}>No trade has been recorded yet</p>
            <p className={styles.stateBody}>
              The ledger was read and is empty. That is a real answer, not a read failure — the
              first offer posted will appear here and never be overwritten.
            </p>
          </div>
        ) : (
          <ul className={styles.ledger}>
            {d.ledger.map((e) => (
              <li key={e.seq} className={styles.entry}>
                <div className={styles.entryHead}>
                  <span className="chip">{e.kind}</span>
                  <span className={styles.entryMove}>
                    {e.ownerBefore || 'nobody'} → {e.ownerAfter || '(no move)'}
                  </span>
                  <span className={styles.entryWhen}>{e.at}</span>
                </div>
                <p className={styles.ruleSays}>
                  {e.scopeLabel || 'scope not recorded'}
                  {e.recordCount != null ? ` · ${e.recordCount} record${e.recordCount === 1 ? '' : 's'} at the time` : ''}
                  {' · '}{e.actorName || e.actorId} ({e.actorRole})
                </p>
                {e.reason ? <p className={styles.ruleQuote}>“{e.reason}”</p> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ================= THE RULES, LABELLED PROPOSED ================== */}
      <section className={PANEL}>
        <p className="eyebrow">The rules — proposed</p>
        <hr className="rule" />
        <p className={styles.stateBody}>
          You said “let’s set the rules”, so these are the starting position and not a decision.
          Each one says whether it came from you, was derived from something you said, or is a
          default picked so the build did not stall.
        </p>
        <div className={styles.controls}>
          <button type="button" className="btn" onClick={() => setShowRules((v) => !v)}>
            {showRules ? 'Hide the rules' : `Show all ${Object.keys(d?.rules || {}).length} rules`}
          </button>
        </div>

        {showRules && d?.rules ? (
          <ul className={styles.ruleList}>
            {Object.entries(d.rules).map(([key, r]) => (
              <li key={key} className={styles.ruleItem}>
                <div className={styles.entryHead}>
                  <span className="chip">{r.status}</span>
                  <strong>{key}</strong>
                </div>
                <p className={styles.ruleSays}>{r.says}</p>
                {r.quote ? <p className={styles.ruleQuote}>“{r.quote}”</p> : null}
                {r.mechanism ? <p className={styles.blocked}>How: {r.mechanism}</p> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {/* ============ WHAT STILL NEEDS BRIAN TO CHOOSE =================== */}
      {d?.openSettings?.length ? (
        <section className={PANEL}>
          <p className="eyebrow">
            Yours to set <span className={styles.sectionCount}>{d.openSettings.length}</span>
          </p>
          <hr className="rule" />
          <p className={styles.stateBody}>
            Every number nobody has decided. Each is a config value, not code — changing one is a
            setting, not a rebuild.
          </p>
          <ul className={styles.ruleList}>
            {d.openSettings.map((s) => (
              <li key={s.key} className={styles.setting}>
                <p className={styles.settingQ}>{s.question}</p>
                <p className={styles.settingNow}>
                  Running on <strong>{String(s.current)}</strong> {s.unit ? `(${s.unit})` : ''} —{' '}
                  <code>{s.key}</code>
                </p>
                <div className={styles.settingOpts}>
                  {(s.options || []).map((o) => (
                    <span className="chip" key={String(o)}>{String(o)}</span>
                  ))}
                </div>
                <p className={styles.blocked}>{s.why}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ==================== GATES ===================================== */}
      {d?.gates?.length ? (
        <section className={PANEL}>
          <p className="eyebrow">
            Waiting on a decision <span className={styles.sectionCount}>{d.gates.length}</span>
          </p>
          <hr className="rule" />
          <ul className={styles.ruleList}>
            {d.gates.map((g) => (
              <li key={g.gate} className={styles.setting}>
                <p className={styles.settingQ}>{g.decisionNeeded}</p>
                <div className={styles.settingOpts}>
                  {(g.options || []).map((o) => (
                    <span className="chip" key={String(o)}>{String(o)}</span>
                  ))}
                </div>
                <p className={styles.blocked}>
                  Blocks: {g.blocks}. Safe meanwhile: {g.safeMeanwhile}.
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  )
}
